import $ from 'jquery';
import { INITIAL_STATE, setConfig, getLocalStorage, setLocalStorage } from './config';
import { debug, setIP, getQueryParamValue, captureClickIds } from './utils';
import render from './render';
import './validation';
import { initZipAutofill, initCountries } from './international';
import { initOnePageCheckout } from './api';
import { initCartAbandonment } from './actions';
import { fireConversion } from './tracking';
import { adaptCoreConfig } from './coreConfig';
import { routeSiteCouponQueries } from './coupon';
// Namespace imports so we can re-expose every framework export as a window
// global (see the Object.assign below), matching the old per-file UMD build.
import * as apiFns from './api';
import * as actionsFns from './actions';
import * as utilsFns from './utils';
import * as configFns from './config';
import * as internationalFns from './international';
import * as trackingFns from './tracking';
import * as constantsFns from './constants';
import * as couponFns from './coupon';
import * as coreHelperFns from './coreHelpers';
import { sapphireCart, sapphireCartOutput, sapphireDisplayProducts } from './sapphireCart';
import { sapphireCartCheck, isSapphireMode } from './sapphire';

let sessionId = '';

var pagePathName = window.location.pathname;
var fname = pagePathName.substring(pagePathName.lastIndexOf('/') + 1);
if (!fname || fname.indexOf('index') != -1) {
  localStorage.removeItem('session_id');
}

// Setting up the SessionID instance
if (!getLocalStorage('session_id')) {
  do {
    sessionId += Math.random().toString(32).substr(2, 9);
  }
  while (sessionId.length < 32);
  setLocalStorage('session_id', (sessionId).slice(0, 32));
} else {

}

INITIAL_STATE["authHeaders"]["X-SESSION-ID"] = getLocalStorage('session_id');
// Setting up the SessionID instance

// Resolves once init() has loaded the operation config. Config-dependent page
// scripts (e.g. buynow.js) use PaymentEngine.ready.then(cfg => ...) so they run
// regardless of how/when the engine and its config load. A late .then() still
// fires (unlike a one-shot event), so ordering never matters.
let markReady;
const ready = new Promise((resolve) => { markReady = resolve; });

// Resolves once render() has run, i.e. once the hidden inputs it builds from the
// query string / PII handoff (#cBillProductIndex, #cShipCountry, ...) actually
// exist. `ready` is deliberately earlier — it fires before the checkout flow —
// so a page script that READS THOSE FIELDS must wait on `rendered`, not `ready`,
// or it sees an empty form and silently falls back to defaults.
let markRendered;
const rendered = new Promise((resolve) => { markRendered = resolve; });

const init = (settings) => {
  // konnektive-core (packages[]) configs are translated here, before anything
  // reads config. Any other config comes back as the same object (1.5.1).
  const configs = { ...INITIAL_STATE, ...adaptCoreConfig(settings) };
  setConfig(configs);
  // Must run after setConfig: it reads getIPURL. At module scope config is
  // still {}, so the lookup hit the current page instead and every Konnektive
  // call fell back to ipAddress 127.0.0.1.
  setIP();
  // G-8: before `ready`, so a page script that checks a coupon from
  // PaymentEngine.ready / konnektive:ready (or on DOM ready) cannot reach #7: a
  // site's own /coupon/query request is answered from the gateway's /coupon.
  // Needs only the config, not the DOM.
  routeSiteCouponQueries();
  console.log('FW Ver.: ' + configs.version);
  console.log('Provider: ' + configs.provider);

  // Announce config is loaded and available — BEFORE the checkout flow so
  // dependent page scripts can run as early as possible. Resolves the ready
  // promise and dispatches a konnektive:ready DOM event (detail = config).
  markReady(configs);
  document.dispatchEvent(new CustomEvent('konnektive:ready', { detail: configs }));

  // Tracking pages: fire the pixel and stop — no checkout flow.
  if (configs.track) {
    fireConversion(configs.track);
    // Still settle `rendered`, or a page script awaiting it hangs forever here.
    markRendered(configs);
    document.dispatchEvent(new CustomEvent('konnektive:rendered', { detail: configs }));
    return;
  }

  // Checkout flow — run after DOM ready so all form elements exist,
  // regardless of when the page called init().
  $(() => {
    try {
      captureClickIds();
      render();
      initCountries();
      initZipAutofill();
      initOnePageCheckout();
      // Must run here, not at module scope: it reads getConfig(), and config is
      // only populated once init() has run.
      initCartAbandonment();
    } finally {
      // Settle `rendered` even if something above threw. Page scripts await it
      // to run their own init, so leaving it pending would turn one engine
      // error into a silently dead page.
      markRendered(configs);
      document.dispatchEvent(new CustomEvent('konnektive:rendered', { detail: configs }));
    }
  });
};

export const PaymentEngine = { init, ready, rendered };

// Replicate the old per-file UMD build: expose every framework export as a
// window global so operation pages and inline handlers (e.g. onchange=
// "combineExpDate()", buynow.js calling getConfig()) keep working.
Object.assign(
  window,
  configFns, utilsFns, actionsFns, apiFns, internationalFns, trackingFns, constantsFns, couponFns,
  { PaymentEngine },
);

// konnektive-core page helpers (1.5.1). Only filled in where the page has not
// defined its own: many funnels' app.js declare a calculateDiscountedPrice /
// getDiscount of their own, and a page that loaded before the engine must not
// have it replaced.
Object.keys(coreHelperFns).forEach((name) => {
  if (typeof window[name] !== 'function') {
    window[name] = coreHelperFns[name];
  }
});

// Sapphire cart (1.6.1): the names Sapphire pages call (demo-engine's
// sapphireCart / sapphireCartOutput / sapphireDisplayProducts), plus a cart
// check and the mode test. Only filled in where the page has not defined its
// own, like the core helpers above; none of them does anything until called.
const SAPPHIRE_GLOBALS = {
  sapphireCart, sapphireCartOutput, sapphireDisplayProducts, sapphireCartCheck, isSapphireMode,
};
Object.keys(SAPPHIRE_GLOBALS).forEach((name) => {
  if (window[name] === undefined) {
    window[name] = SAPPHIRE_GLOBALS[name];
  }
});

// One-tag connection: read this bundle's own <script data-config="..."> and
// load that local config file (which calls PaymentEngine.init(...)). Loaded
// asynchronously — timing no longer matters because config-dependent page
// scripts wait on PaymentEngine.ready / the konnektive:ready event.
(function loadOpConfig() {
  const self = document.currentScript || document.querySelector('script[data-config]');
  const cfg = self && self.getAttribute('data-config');
  if (!cfg) return; // no data-config -> page calls PaymentEngine.init() itself
  const s = document.createElement('script');
  s.src = cfg;
  document.head.appendChild(s);
}());
